import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import YearSelector from '../../../Common/Selectors/YearSelector';
import { financialMonth, financialYear } from '../../../Utils/Functions';
import { creators as YearlyFinancialYTDActions } from '../../../Reducers/Reporting/YearlyFinancialYTD';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import BottomChart from './BottomChart';

const YearlyFinancialPerformance = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  tableStructure,
  selectedCompany,
  tabIndex,
  profitSetting,
  yearlyFinancialPerformanceSetting,
  bubbleFlag
}) => {
  const dispatch = useDispatch();
  const yfpSummary = useSelector((state) => state.Reporting.Summary.reportingSummaries?.yearlyFinancialYTD ?? '');
  const summaryData = useSelector((state) => state.Reporting.yearlyfinancialYTD.summaryData);
  const detailData = useSelector((state) => state.Reporting.yearlyfinancialYTD.detailData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const isMountedRef = useRef(false);
  const [resize, setResize] = useState(false);
  const [label, setLabel] = useState('');
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedOption, setSelectedOption] = useState('Forecast');
  const [showPercent, setShowPercent] = useState(false);
  const [profitName, setProfitName] = useState('Profit / Loss');
  const [pprofitName, setPprofitName] = useState('% Profit');

  // Redux action creators
  const getYearlyFinancialYTDSummary = useCallback((selectedYears) => {
    dispatch(YearlyFinancialYTDActions.yearlyytdSummaryRequest(selectedYears));
  }, [dispatch]);

  const getYearlyFinancialYTDDetail = useCallback((selectedYears, defaultYear, defaultMonth, DateKey) => {
    dispatch(YearlyFinancialYTDActions.yearlyytdDetailRequest(selectedYears, defaultYear, defaultMonth, DateKey));
  }, [dispatch]);

  const reportSettingUpdateRequest = useCallback((update_setting) => {
    dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting));
  }, [dispatch]);

  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prevResize => !prevResize);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    window.addEventListener('resize', onResize);

    let _selectYears = [];
    let _selectTopItems = [];
    let _selectLabels = [];

    let savedYears = appdata.getLocalItem('yfpTopItems');

    if (savedYears) {
      for (let i = 0; i < savedYears.length; i++) {
        _selectYears.push(savedYears[i].year);
        _selectTopItems.push({ year: savedYears[i].year });
        _selectLabels.push(savedYears[i].year);
      }
    } else {
      for (let i = defaultYear - 3; i <= defaultYear; i++) {
        if (i >= 2008) {
          _selectYears.push(i);
          _selectTopItems.push({ year: i });
          _selectLabels.push(i);
        }
      }
    }

    setSelectedYears(_selectYears);
    setLabel(_selectYears.length === 6 ? 'All' : 'Multi');
    setSelectedTopItems(_selectTopItems);
    setSelectedLabels(_selectLabels);

    if (yearlyFinancialPerformanceSetting) {
      const parsedSetting = JSON.parse(yearlyFinancialPerformanceSetting);
      setSelectedOption(parsedSetting.selectedOption);
      setShowPercent(parsedSetting.showPercent);
    } else {
      reportSettingUpdateRequest({
        YearlyFinancialPerformance: JSON.stringify({
          selectedOption: 'Forecast',
          showPercent: false,
        }),
      });
    }

    if (profitSetting) {
      const parsedProfitSetting = JSON.parse(profitSetting);
      setProfitName(parsedProfitSetting.profitName);
      setPprofitName(parsedProfitSetting.pprofitName);
    } else {
      reportSettingUpdateRequest({
        ProfitTitle: JSON.stringify({
          profitName: 'Profit / Loss',
          pprofitName: '% Profit',
        }),
      });
    }

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) return;

    let _selectYears = [];
    let _selectTopItems = [];
    let _selectLabels = [];

    for (let i = defaultYear - 3; i <= defaultYear; i++) {
      if (i >= 2008) {
        _selectYears.push(i);
        _selectTopItems.push({ year: i });
        _selectLabels.push(i);
      }
    }
    _selectYears.sort((a, b) => a - b);
    
    setSelectedYears(_selectYears);
    setLabel('Multi');
    setSelectedTopItems(_selectTopItems);
    setSelectedLabels(_selectLabels);
    appdata.setLocalItem('yfpTopItems', _selectTopItems);
  }, [defaultYear, defaultStartMonth]);

  const triggerYearlyFinancialRequests = useCallback(() => {
    let _selectYears = [];
    for (let i = defaultYear - 3; i <= defaultYear; i++) {
      if (i >= 2008) {
        _selectYears.push(i);
      }
    }
    _selectYears.sort((a, b) => a - b);

    let month = financialMonth(defaultMonth, defaultStartMonth);
    let year = financialYear(defaultYear, month, defaultStartMonth);

    getYearlyFinancialYTDSummary(_selectYears);
    getYearlyFinancialYTDDetail(_selectYears, year, month, [defaultDimDate]);
  }, [defaultYear, defaultMonth, defaultStartMonth, defaultDimDate, getYearlyFinancialYTDSummary, getYearlyFinancialYTDDetail]);

  useEffect(() => {
    if (!isMountedRef.current) return;

    if (!lastFetchedCompany || !lastFetchedDimDate) {
      triggerYearlyFinancialRequests();
    }
  }, [lastFetchedCompany, lastFetchedDimDate, triggerYearlyFinancialRequests]);

  useEffect(() => {
    if (!isMountedRef.current) return;

    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (
      valuesChanged &&
      lastFetchedCompany &&
      lastFetchedDimDate
    ) {
      triggerYearlyFinancialRequests();
    }
  }, [selectedCompany, defaultDimDate, lastFetchedCompany, lastFetchedDimDate, triggerYearlyFinancialRequests]);

  useEffect(() => {
    if (profitSetting) {
      const parsedProfitSetting = JSON.parse(profitSetting);
      setProfitName(parsedProfitSetting.profitName);
      setPprofitName(parsedProfitSetting.pprofitName);
    }
  }, [tabIndex, profitSetting]);

  const handleYear = useCallback(async (event) => {
    let _selectYears = [];
    let _selectTopItems = [];
    let _selectLabels = [];

    let years = event.selectedYears;
    years.forEach((year) => {
      if (year !== 'All') {
        _selectYears.push(year);
        _selectTopItems.push({ year: year });
        _selectLabels.push(year);
      }
    });
    _selectYears.sort((a, b) => a - b);
    
    setSelectedYears(_selectYears);
    setLabel('Multi');
    setSelectedTopItems(_selectTopItems);
    setSelectedLabels(_selectLabels);
    appdata.setLocalItem('yfpTopItems', _selectTopItems);

    handleFilter(event);
    let month = financialMonth(defaultMonth, defaultStartMonth);
    let year = financialYear(defaultYear, month, defaultStartMonth);
    if (years.length > 0) {
      getYearlyFinancialYTDSummary(_selectYears);
      getYearlyFinancialYTDDetail(_selectYears, year, month, [defaultDimDate]);
    } else toast.warning('Please select the month');
  }, [getYearlyFinancialYTDSummary, getYearlyFinancialYTDDetail]);

  const handleFilter = useCallback(async (event) => {
    // Update state based on event
    if (event.selectedTopItems !== undefined) {
      setSelectedTopItems(event.selectedTopItems);
    }
    if (event.selectedLabels !== undefined) {
      setSelectedLabels(event.selectedLabels);
    }
    if (event.selectedOption !== undefined) {
      setSelectedOption(event.selectedOption);
    }
    if (event.showPercent !== undefined) {
      setShowPercent(event.showPercent);
    }
    if (event.profitName !== undefined) {
      setProfitName(event.profitName);
    }
    if (event.pprofitName !== undefined) {
      setPprofitName(event.pprofitName);
    }

    if (event.selectedTopItems) {
      let _selectTopItems = [];
      let _selectYears = [];
      if (event.selectedTopItems.length === 0) {
        for (
          let i = defaultYear - 3;
          i <= defaultYear;
          i++
        ) {
          _selectTopItems.push({ year: i });
          _selectYears.push(i);
        }
        setSelectedTopItems(_selectTopItems);
        setSelectedYears(_selectYears);
        appdata.setLocalItem('yfpTopItems', _selectTopItems);
      } else {
        appdata.setLocalItem('yfpTopItems', event.selectedTopItems);
        event.selectedTopItems.forEach((item) => _selectYears.push(item.year));
        setSelectedTopItems(event.selectedTopItems);
        setSelectedYears(_selectYears);
      }
    }

    if (event.selectedOption || event.showPercent !== undefined)
      await reportSettingUpdateRequest({
        YearlyFinancialPerformance: JSON.stringify({
          selectedOption: event.selectedOption
            ? event.selectedOption
            : selectedOption,
          showPercent:
            event.showPercent !== undefined
              ? event.showPercent
              : showPercent,
        }),
      });

    if (event.profitName || event.pprofitName)
      await reportSettingUpdateRequest({
        ProfitTitle: JSON.stringify({
          profitName: event.profitName
            ? event.profitName
            : profitName,
          pprofitName: event.pprofitName
            ? event.pprofitName
            : pprofitName,
        }),
      });
  }, [defaultYear, selectedOption, showPercent, profitName, pprofitName, reportSettingUpdateRequest]);

  return (
    <div style={{ height: '89vh', overflowX: 'hidden' }}>
      <div className="wrapper" id="yfp_year_selector">
        <YearSelector
          selectedYears={selectedYears}
          label={label}
          onChange={handleYear}
        />
      </div>
      <div>
        <TopChart
          summaryData={summaryData}
          selectedYears={selectedYears}
          selectedTopItems={selectedTopItems}
          selectedLabels={selectedLabels}
          defaultStartMonth={defaultStartMonth}
          handleFilter={handleFilter}
        />
        <BottomChart
          detailData={detailData}
          selectedTopItems={selectedTopItems}
          selectedYears={selectedYears}
          tableStructure={tableStructure}
          selectedOption={selectedOption}
          showPercent={showPercent}
          profitName={profitName}
          pprofitName={pprofitName}
          handleFilter={handleFilter}
        />
      </div>

      {bubbleFlag && yfpSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed', 
            bottom: 60, 
            right: 30, 
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={yfpSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

YearlyFinancialPerformance.propTypes = {
  tableStructure: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  tabIndex: PropTypes.number.isRequired,
  yearlyFinancialPerformanceSetting: PropTypes.string.isRequired,
  profitSetting: PropTypes.string.isRequired,
};

export default YearlyFinancialPerformance;
